/**
 * EdgeDiagnosticsWorkspace — Platforma → Diagnostika. Shows the network path of the admin's OWN request as the
 * API receives it (`GET /v1/management/diagnostics/request`): the socket peer and the proxy headers. It answers
 * "does the proxy hand the API real client addresses, and which header can be trusted?" without a shell on the
 * host. Open it from a few networks; the explanation below the values says how to read the result.
 */

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { DetailField, DetailFields, DetailSection } from '@/components/DetailSection';
import { EmptyState } from '@/components/EmptyState';
import { Skeleton } from '@/components/Skeleton';
import { Toolband } from '@/components/Toolband';
import { ApiError } from '@/lib/api';
import { useEdgeDiagnosticsQuery } from '@/modules/management/api';
import { EDGE_SOURCE_LABEL, readEdge } from './edge';

interface EdgeDiagnosticsWorkspaceProps {
  isAdmin: boolean;
}

export function EdgeDiagnosticsWorkspace({ isAdmin }: EdgeDiagnosticsWorkspaceProps) {
  const query = useEdgeDiagnosticsQuery(isAdmin);
  const accessDenied = query.error instanceof ApiError && query.error.status === 403;
  const view = query.data;
  const reading = view ? readEdge(view) : null;

  return (
    <div data-testid="edge-diagnostics-workspace" className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Toolband>
        <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-3">
          Cesta požadavku · tento požadavek
        </span>
        <div className="ml-auto">
          <Button
            variant="secondary"
            size="sm"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Obnovit
          </Button>
        </div>
      </Toolband>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">
        {query.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : accessDenied ? (
          <EmptyState eyebrow="403" title="Přístup odepřen" description="Vyžaduje administrátorská práva." />
        ) : query.isError || !view || !reading ? (
          <EmptyState eyebrow="Chyba" title="Diagnostiku nelze načíst" description="Zkuste obnovit stránku." />
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <DetailSection title="Co API vidí">
              <DetailFields cols={2}>
                <DetailField label="Adresa klienta podle proxy" span>
                  {reading.client ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <span data-testid="edge-client" className="font-mono text-[12.5px]">
                        {reading.client}
                      </span>
                      {reading.source && <Badge tone="accent">{EDGE_SOURCE_LABEL[reading.source]}</Badge>}
                    </span>
                  ) : (
                    <span className="font-normal italic text-ink-3">žádná adresa</span>
                  )}
                </DetailField>
                <DetailField label="Připojil se (socket)" value={view.peer} mono />
                <DetailField label="X-Real-IP" value={view.x_real_ip} mono />
                <DetailField label="X-Forwarded-For" span>
                  {view.x_forwarded_for.length === 0 ? (
                    <span className="font-normal italic text-ink-3">—</span>
                  ) : (
                    <ol data-testid="edge-xff" className="flex flex-col gap-1">
                      {view.x_forwarded_for.map((hop, i) => {
                        const last = i === view.x_forwarded_for.length - 1;
                        return (
                          <li key={`${i}-${hop}`} className="flex items-center gap-2 font-mono text-[12.5px]">
                            <span>{hop}</span>
                            {last ? <Badge tone="ok" size="sm">zaznamenala proxy</Badge> : <Badge tone="neutral" size="sm">odeslal klient / další proxy</Badge>}
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </DetailField>
                <DetailField label="Forwarded" value={view.forwarded} mono />
                <DetailField label="X-Forwarded-Proto" value={view.x_forwarded_proto} mono />
                <DetailField label="X-Forwarded-Host" value={view.x_forwarded_host} mono />
                <DetailField label="Host" value={view.host} mono />
              </DetailFields>
              {reading.earlierHops.length > 0 && (
                <p data-testid="edge-earlier-hops" className="mt-4 border-t border-line pt-3 text-[12.5px] leading-snug text-ink-2">
                  <Badge tone="warn" size="sm">pozor</Badge>{' '}
                  Řetězec obsahuje dřívější adresy ({reading.earlierHops.join(', ')}). Proxy je převzala z příchozího požadavku;
                  věřit lze jen poslednímu záznamu.
                </p>
              )}
            </DetailSection>

            <DetailSection title="Jak výsledek číst">
              <ul className="flex flex-col gap-2.5 text-[12.5px] leading-snug text-ink-2">
                <li>Spojení s API navazuje proxy (Traefik), proto je „Připojil se“ adresa proxy, ne klienta.</li>
                <li>
                  Důvěryhodný je jen <strong className="font-semibold text-ink">poslední</strong> záznam X-Forwarded-For
                  — připojila ho proxy. Dřívější záznamy mohl poslat klient.
                </li>
                <li>
                  Otevřete tuto stránku z více sítí (kancelář, mobilní data). Liší-li se poslední záznam, proxy vidí skutečné
                  adresy klientů a limit podle zdrojové adresy dává smysl. Je-li všude stejný, omezil by všechny uživatele
                  společně.
                </li>
              </ul>
            </DetailSection>
          </div>
        )}
      </div>
    </div>
  );
}
